import {
  createPublicNavItems,
  ExternalLinkButton,
  GlassCard,
  SectionHeader,
  SiteNav,
  VisualShell
} from "@jinze-ai/ui";
import { MapPin } from "lucide-react";
import { profile, type TimelineEntry } from "./content/profile";

const navItems = createPublicNavItems();

export function App() {
  return (
    <VisualShell>
      <SiteNav
        activeLabel="Me"
        brand={{
          label: "me.jinze.ai",
          href: "https://me.jinze.ai",
          ariaLabel: "me.jinze.ai home"
        }}
        items={navItems}
      />

      <main>
        <section className="hero section-reveal" aria-labelledby="hero-title">
          <div className="hero-copy">
            <p className="eyebrow">{profile.hero.role}</p>
            <h1 id="hero-title">{profile.hero.title}</h1>
            <p className="intro">{profile.hero.intro}</p>
            <div className="hero-meta" aria-label="Profile details">
              <span>
                <MapPin aria-hidden="true" size={16} />
                {profile.hero.location}
              </span>
              <span>{profile.hero.email}</span>
            </div>
            <div className="hero-focus" aria-label="Current focus areas">
              {profile.focus.map((focus) => (
                <span key={focus}>{focus}</span>
              ))}
            </div>
            <div className="hero-actions">
              <ExternalLinkButton href={profile.hero.github}>
                View GitHub
              </ExternalLinkButton>
            </div>
          </div>
        </section>

        <TimelineSection
          id="work"
          label="Experience"
          title="Systems that turn messy data into usable decisions."
          items={profile.experience}
        />

        <TimelineSection
          id="projects"
          label="Selected work"
          title="Agentic workflows, retrieval systems, and applied ML infrastructure."
          items={profile.projects}
        />

        <div className="stacked-sections">
          <TimelineSection
            label="Education"
            title="Formal training across computer science, engineering, and applied systems."
            items={profile.education}
          />
          <TimelineSection
            label="Publications"
            title="Research-facing work at the boundary of AI and applied domain problems."
            items={profile.publications}
          />
          <TimelineSection
            label="Patents"
            title="Applied invention records from agricultural intelligence and field data systems."
            items={profile.patents}
          />
          <TimelineSection
            label="Skills"
            title="Tools and systems I reach for often."
            items={profile.skills}
            variant="chips"
          />
        </div>
      </main>

      <footer className="site-footer">© 2026 Jinze.</footer>
    </VisualShell>
  );
}

type TimelineSectionProps = {
  id?: string;
  label: string;
  title: string;
  items: TimelineEntry[];
  variant?: "list" | "chips";
};

function TimelineSection({ id, label, title, items, variant = "list" }: TimelineSectionProps) {
  const titleId = `${label.toLowerCase().replace(/\s+/g, "-")}-title`;

  return (
    <section className="section-grid section-reveal" id={id} aria-labelledby={titleId}>
      <div className="section-head">
        <SectionHeader label={label} title={title} id={titleId} />
      </div>
      <div className="timeline">
        {items.map((item) => (
          <GlassCard
            className="timeline-item"
            key={`${item.title}-${item.subtitle ?? ""}`}
          >
            <div>
              <h3>{item.title}</h3>
              {item.subtitle ? <p>{item.subtitle}</p> : null}
            </div>
            {item.time || item.place || item.venue ? (
              <div className="timeline-meta">
                {[item.time, item.place, item.venue].filter(Boolean).map((detail) => (
                  <span key={detail}>{detail}</span>
                ))}
              </div>
            ) : null}
            {item.tags.length > 0 ? (
              <div className="timeline-tags" aria-label={`${item.title} tags`}>
                {item.tags.map((tag) => (
                  <span key={tag}>{tag}</span>
                ))}
              </div>
            ) : null}
            {item.image ? (
              <figure className="timeline-image">
                <img src={item.image.src} alt={item.image.alt} loading="lazy" />
              </figure>
            ) : null}
            {variant === "chips" && item.points.length > 0 ? (
              <div className="timeline-chips">
                {item.points.map((point) => (
                  <span key={point}>{point}</span>
                ))}
              </div>
            ) : null}
            {variant === "list" && item.points.length > 0 ? (
              <ul>
                {item.points.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            ) : null}
            {item.link ? (
              <ExternalLinkButton className="timeline-link" href={item.link}>
                Open work
              </ExternalLinkButton>
            ) : null}
          </GlassCard>
        ))}
      </div>
    </section>
  );
}
